import React, { useMemo, useState } from "react";

import CreateOutlinedIcon from "@mui/icons-material/CreateOutlined";
import { Button, Fade, IconButton, Popover, Stack, Typography } from "@mui/material";
import { MaterialTooltip } from "components/tooltips/MaterialTooltip";
import { useDealActions, useDealCSSelector, useDealSelector, usePickUserPermissions } from "store";
import { ICapitalSeeker, IRole, UserPermissionType } from "store/types";
import { isRoleAnalyst } from "utils/user";

import { PopupEmptyState } from "../PopupEmptyState";
import { ContactItem } from "./ContactItem";

interface IContact {
  role: IRole | null;
}

export const getFullName = (contact: ICapitalSeeker): string => {
  if (!contact) return "";

  const first = contact.first_name || "";
  const last = contact.last_name || "";

  return `${first} ${last}` || "";
};

export const Contact: React.FC<IContact> = ({ role }) => {
  const capitalSeekers = useDealCSSelector();
  const { setCharacteristicsManageAction } = useDealActions();
  const { id: dealId, title: dealTitle } = useDealSelector();

  const myDeals = usePickUserPermissions("deals");
  const noAccessPermission = myDeals && myDeals === UserPermissionType.NO_ACCESS;
  const isViewerPermission = myDeals && myDeals === UserPermissionType.VIEWER;

  const isAdmin = isRoleAnalyst({ role, preventUser: noAccessPermission || isViewerPermission });

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? "deal-asset-popover" : undefined;

  const contactTitle = useMemo(() => {
    if (!capitalSeekers.length) return "N/A";
    if (capitalSeekers.length === 1) return getFullName(capitalSeekers[0]) || "N/A";
    if (capitalSeekers.length > 1) return "Multiple";
  }, [capitalSeekers]);

  return (
    <>
      <Button
        sx={{
          color: "#173c4c",
          padding: "0 10px",
          minWidth: "auto",
          fontSize: "15px",
          backgroundColor: "#edf1f9",
          minHeight: "28px",
          maxHeight: "28px"
        }}
        onClick={handleClick}
        className="trigger"
      >
        <Stack
          direction="row"
          sx={{
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            overflow: "hidden",
            maxWidth: "200px",
            fontSize: "12px",
            fontWeight: "400"
          }}
          gap="5px"
        >
          <Typography sx={{ color: "#496682" }}>Contact:</Typography>{" "}
          <Typography sx={{ color: "#071F35", textOverflow: "ellipsis", whiteSpace: "nowrap", overflow: "hidden" }}>
            {contactTitle}
          </Typography>
        </Stack>
      </Button>

      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left"
        }}
      >
        <Fade timeout={600} in={open}>
          <Stack gap="20px" width="450px" borderRadius={8} bgcolor="#ffffff" padding="20px 20px 20px 25px">
            {isAdmin && (
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Stack sx={{ width: "100%" }} gap="10px" direction="row" alignItems="center">
                  <Typography color="#5d7782">Edit</Typography>
                  <IconButton
                    onClick={() => {
                      handleClose();
                      setCharacteristicsManageAction({
                        dealId: dealId,
                        dealName: dealTitle,
                        isContact: true
                      });
                    }}
                  >
                    <MaterialTooltip title="Edit contacts" isShow>
                      <CreateOutlinedIcon />
                    </MaterialTooltip>
                  </IconButton>
                </Stack>
              </Stack>
            )}

            {!isAdmin && !capitalSeekers.length ? (
              <PopupEmptyState text="No contacts linked to this deal yet." />
            ) : (
              <Stack gap="20px" sx={{ maxHeight: "400px", overflowY: "auto" }}>
                {capitalSeekers.map((contact) => (
                  <ContactItem contact={contact} key={contact.id} />
                ))}
              </Stack>
            )}

            <Stack direction="row" justifyContent="flex-end" alignItems="center" gap="16px">
              <Button variant="outlined" onClick={handleClose}>
                Close
              </Button>
            </Stack>
          </Stack>
        </Fade>
      </Popover>
    </>
  );
};
